import { BedDouble, Clock3, Crown, MapPin, Phone, Search, ShoppingBag, UtensilsCrossed, X } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { BottomSheet } from "@/components/ui/bottom-sheet";

export function StorefrontHeader({ outlet, orderType, tableNumber, roomNumber, onSearch }) {
  const logo = outlet.logo_file_id ? fileUrl(outlet.logo_file_id) : outlet.logo_url;
  const location = orderType === "room" ? roomNumber && `Kamar ${roomNumber}` : tableNumber && `Meja ${tableNumber}`;
  return (
    <header className="storefront-header">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <h1 className="min-w-0 truncate font-display text-[22px] font-medium leading-7 text-heritage" data-testid="outlet-name">{outlet.name}</h1>
          {location && <span className="storefront-location"><MapPin className="h-3 w-3" aria-hidden="true" />{location}</span>}
        </div>
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-heritage" />Sajian Utama</p>
      </div>
      <button type="button" onClick={onSearch} aria-label="Cari hidangan" data-testid="header-search-btn" className="grid h-11 w-11 shrink-0 place-items-center rounded-full hover:bg-accent"><Search className="h-5 w-5" /></button>
      {logo && <img src={logo} alt={`Logo ${outlet.name}`} className="h-9 w-9 shrink-0 rounded-full border-2 border-heritage/20 bg-white object-contain p-0.5" />}
    </header>
  );
}

export function StorefrontHero({ outlet, image, orderType, mode, roomNumber, tableNumber }) {
  const logo = outlet.logo_file_id ? fileUrl(outlet.logo_file_id) : outlet.logo_url;
  const Icon = orderType === "room" ? BedDouble : mode === "takeaway" ? ShoppingBag : UtensilsCrossed;
  return (
    <section className="storefront-hero" aria-label="Informasi pemesanan">
      <img src={image} alt="Suasana ruang makan bergaya Jawa" className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
      <div className="storefront-hero-shade" />
      <div className="absolute inset-x-4 top-4 flex items-start justify-between gap-3">
        <div className="storefront-stamp"><Crown className="h-4 w-4 shrink-0 text-[#7e5713]" /><span className="line-clamp-1">{outlet.tagline || outlet.name}</span></div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-background/95 font-display text-[22px] text-heritage shadow-sm" aria-hidden="true">
          {logo ? <img src={logo} alt="" className="h-full w-full rounded-full object-contain p-0.5" /> : outlet.name?.charAt(0)}
        </span>
      </div>
      <div className="absolute inset-x-4 bottom-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="storefront-badge bg-[#ffddb1]/95 text-[#614000]"><UtensilsCrossed className="h-3.5 w-3.5" />Pemesanan QR</span>
          {outlet.hours && <span className="storefront-badge bg-white/95 text-muted-foreground"><Clock3 className="h-3.5 w-3.5 text-heritage" />{outlet.hours}</span>}
        </div>
        <div className="storefront-service">
          <div className="flex min-w-0 items-center gap-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#ffddb1]/60 text-[#7e5713]"><Icon className="h-[18px] w-[18px]" /></span>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#7e5713]">{orderType === "room" ? "Layanan Kamar" : mode === "takeaway" ? "Bawa Pulang" : "Santap di Tempat"}</p>
              <p className="truncate text-[13px] font-semibold">
                {orderType === "room" ? <span data-testid="room-number-display">{roomNumber ? `Kamar ${roomNumber}` : "Nomor kamar diisi saat checkout"}</span> : tableNumber ? <span data-testid="table-number-display">Meja {tableNumber}</span> : <span>{outlet.name}</span>}
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-[#ffdbd0] px-2.5 py-1 text-[10px] font-semibold text-[#822805]">Pesan Online</span>
        </div>
      </div>
    </section>
  );
}

export function OutletServiceSheet({ outlet, onClose }) {
  return (
    <BottomSheet title="Layanan Outlet" description="Alamat, jam operasional, dan kontak outlet Anda." onClose={onClose}>
      <div className="flex items-center justify-between border-b border-border p-5">
        <h2 className="font-display text-[28px] font-medium">Layanan Outlet</h2>
        <button type="button" onClick={onClose} aria-label="Tutup layanan" className="grid h-10 w-10 place-items-center rounded-full bg-accent"><X className="h-4 w-4" /></button>
      </div>
      <div className="sheet-scroll space-y-5 p-5">
        <h3 className="font-display text-2xl">{outlet.name}</h3>
        {outlet.address && <p className="flex items-start gap-3 text-sm"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-heritage" />{outlet.address}</p>}
        {outlet.hours && <p className="flex items-center gap-3 text-sm"><Clock3 className="h-4 w-4 text-heritage" />{outlet.hours}</p>}
        {outlet.phone && <a href={`tel:${outlet.phone.replace(/[^+\d]/g, "")}`} className="flex min-h-11 items-center gap-3 text-sm text-heritage underline"><Phone className="h-4 w-4" />{outlet.phone}</a>}
        <div className="rounded-2xl bg-accent p-4 text-[13px] leading-relaxed">Sampaikan preferensi atau pantangan bahan pada catatan hidangan sebelum menambahkan sajian ke pesanan.</div>
      </div>
    </BottomSheet>
  );
}
